/**
 * Matter API Service
 *
 * Handles matter CRUD and relationships (Phase 10 - Matter Layer Architecture)
 */

import { apiClient } from './client';
import {
  Matter,
  MatterWithCounts,
  CreateMatterData,
  UpdateMatterData,
  MatterDocumentsResponse,
  MatterWorkflowsResponse,
  CreateWorkflowInMatterData,
  Document,
  Project
} from '../../types';

class MatterAPI {
  /**
   * Get all matters for the authenticated user
   * @returns List of matters with document and workflow counts
   */
  async getMatters(): Promise<MatterWithCounts[]> {
    const r = await apiClient.get<{ matters: MatterWithCounts[], total_count: number }>('/api/v1/matters');
    return Array.isArray(r.data.matters) ? r.data.matters : [];
  }

  /**
   * Get a specific matter by ID
   * @param matterId - The ID of the matter to retrieve
   * @returns Matter details
   */
  async getMatter(matterId: string): Promise<Matter> {
    const response = await apiClient.get<Matter>(`/api/v1/matters/${matterId}`);
    return response.data;
  }

  /**
   * Create a new matter
   * @param data - Matter creation data (name, description)
   * @returns Created matter
   */
  async createMatter(data: CreateMatterData): Promise<Matter> {
    const response = await apiClient.post<Matter>('/api/v1/matters', data);
    return response.data;
  }

  /**
   * Update an existing matter
   * @param matterId - The ID of the matter to update
   * @param data - Matter update data (name, description, status)
   * @returns Updated matter
   */
  async updateMatter(matterId: string, data: UpdateMatterData): Promise<Matter> {
    const response = await apiClient.put<Matter>(`/api/v1/matters/${matterId}`, data);
    return response.data;
  }

  /**
   * Delete a matter and all its related data
   * @param matterId - The ID of the matter to delete
   */
  async deleteMatter(matterId: string): Promise<void> {
    await apiClient.delete(`/api/v1/matters/${matterId}`);
  }

  /**
   * Get documents in a matter.
   *
   * Issue #698 §A: the API defaults to `upload_status='completed'` only.
   * Callers that need non-completed rows (the upload progress poller is the
   * canonical case) pass `includeStatuses` to opt in; the server returns 400
   * for unknown values, so this list must match the documented allowlist.
   *
   * @param matterId - The ID of the matter
   * @param includeStatuses - Optional opt-in for non-completed docs
   */
  async getMatterDocuments(
    matterId: string,
    includeStatuses?: ReadonlyArray<string>,
  ): Promise<MatterDocumentsResponse> {
    const config =
      includeStatuses && includeStatuses.length > 0
        ? { params: { include_statuses: [...includeStatuses].sort().join(',') } }
        : undefined;
    const response = await apiClient.get<MatterDocumentsResponse>(
      `/api/v1/matters/${matterId}/documents`,
      config,
    );
    return response.data;
  }

  /**
   * Upload a document to a matter
   * @param matterId - The ID of the matter
   * @param file - The file to upload
   * @param onUploadProgress - Optional progress callback
   * @returns Uploaded document
   */
  async uploadDocumentToMatter(
    matterId: string,
    file: File,
    onUploadProgress?: (progress: number) => void
  ): Promise<Document> {
    // Validate file before upload
    const maxSize = 100 * 1024 * 1024; // 100MB
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    const allowedExtensions = ['.pdf', '.docx'];

    if (file.size > maxSize) {
      throw new Error(`File "${file.name}" is too large. Maximum size is 100MB.`);
    }

    const isValidType = allowedTypes.includes(file.type) ||
      allowedExtensions.some(ext => file.name.toLowerCase().endsWith(ext));

    if (!isValidType) {
      throw new Error(`File "${file.name}" is not supported. Please upload PDF or Word documents only.`);
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await apiClient.post<{message: string, document: Document}>(
        `/api/v1/matters/${matterId}/documents`,
        formData,
        {
          headers: {
            // Remove Content-Type to let browser set it with boundary
            'Content-Type': undefined,
          },
          onUploadProgress: onUploadProgress ? (progressEvent) => {
            if (progressEvent.total) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              onUploadProgress(progress);
            }
          } : undefined,
          timeout: 300000, // 5 minutes for large files
        }
      );
      return response.data.document; // Return just the document part
    } catch (error: any) {
      if (error.response?.status === 413) {
        throw new Error('File is too large. Please upload a smaller file.');
      } else if (error.response?.status === 415) {
        throw new Error('File type not supported. Please upload PDF or Word documents only.');
      } else if (error.code === 'ECONNABORTED') {
        throw new Error('Upload timed out. Please try again with a smaller file.');
      } else if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      throw error;
    }
  }

  /**
   * Get all workflows in a matter
   * @param matterId - The ID of the matter
   * @returns Matter workflows with metadata
   */
  async getMatterWorkflows(matterId: string): Promise<MatterWorkflowsResponse> {
    const r = await apiClient.get<MatterWorkflowsResponse>(`/api/v1/matters/${matterId}/workflows`);
    return r.data;
  }

  /**
   * Create a new workflow in a matter
   * @param matterId - The ID of the matter
   * @param data - Workflow creation data (name, workflow_type)
   * @returns Created workflow (Project)
   */
  async createWorkflowInMatter(matterId: string, data: CreateWorkflowInMatterData): Promise<Project> {
    const response = await apiClient.post<Project>(`/api/v1/matters/${matterId}/workflows`, data);
    return response.data;
  }

  /**
   * Task 219: Process a document for RAG (create searchable chunks)
   * This enables the chat to search matter documents via semantic search.
   * @param matterId - The ID of the matter
   * @param documentId - The ID of the document to process
   * @returns Processing result with chunk count
   */
  async processDocumentForRag(matterId: string, documentId: string): Promise<{
    status: 'processed' | 'already_processed';
    document_id: string;
    filename?: string;
    chunk_count: number;
    message: string;
  }> {
    const response = await apiClient.post(`/api/v1/matters/${matterId}/documents/${documentId}/process-for-rag`);
    return response.data;
  }
}

export const matterApi = new MatterAPI();
